import { useState, useEffect } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  FileSpreadsheet, 
  FileText,
  Calendar, 
  TrendingDown, 
  Wallet, 
  Building2, 
  Users,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
const API = `${BACKEND_URL}/api`;

const ExpenseLedger = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('this_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('by-group');
  
  const [ledgerData, setLedgerData] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [groupData, setGroupData] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  
  // For filter dropdowns
  const [allGroups, setAllGroups] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);

  const getDateRange = () => {
    const today = new Date();
    let startDate, endDate;

    // Helper to format date as YYYY-MM-DD in local timezone (not UTC)
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    switch (dateFilter) {
      case 'today':
        startDate = today;
        endDate = today;
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        startDate = yesterday;
        endDate = yesterday;
        break;
      case 'this_week':
        const dayOfWeek = today.getDay();
        startDate = new Date(today);
        startDate.setDate(today.getDate() - dayOfWeek);
        endDate = today;
        break;
      case 'this_month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
        break;
      case 'last_month':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'this_year':
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = today;
        break;
      case 'custom':
        startDate = customStartDate ? new Date(customStartDate) : new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = customEndDate ? new Date(customEndDate) : today;
        break;
      default:
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
    }

    return {
      start: formatLocalDate(startDate),
      end: formatLocalDate(endDate)
    };
  };

  // Fetch groups and categories for filter dropdowns
  const fetchFilters = async () => {
    try {
      const token = localStorage.getItem('token');
      const dates = getDateRange();
      
      // Fetch both: deduction-groups (for category mapping) and expense-summary (for actual groups in data)
      const [groupsRes, categoriesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/deduction-groups`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/expense-categories`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reports/expense-summary/by-group?start_date=${dates.start}&end_date=${dates.end}`, 
          { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      // Use groups from actual expense data (normalized), not from deduction-groups
      // This ensures the group filter matches the data
      const dataGroups = (summaryRes.data?.by_group || []).map(g => ({
        id: g.group,
        name: g.group
      }));
      
      setAllGroups(dataGroups);
      setAllCategories(categoriesRes.data);
      setFilteredCategories(categoriesRes.data);
    } catch (error) {
      console.error('Failed to fetch filters', error);
    }
  };

  // Update filtered categories when group changes
  useEffect(() => {
    if (groupFilter === 'all') {
      setFilteredCategories(allCategories);
    } else {
      setFilteredCategories(allCategories.filter(c => c.group === groupFilter));
    }
    // Reset category filter when group changes
    setCategoryFilter('all');
  }, [groupFilter, allCategories]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const { start, end } = getDateRange();
      const source = sourceFilter === 'all' ? undefined : sourceFilter;
      const group = groupFilter === 'all' ? undefined : groupFilter;
      const category = categoryFilter === 'all' ? undefined : categoryFilter;

      const [ledgerRes, categoryRes, groupRes] = await Promise.all([
        axios.get(`${API}/reports/expense-ledger`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { start_date: start, end_date: end, source, group, category }
        }),
        axios.get(`${API}/reports/expense-summary/by-category`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { start_date: start, end_date: end, source, group, category }
        }),
        axios.get(`${API}/reports/expense-summary/by-group`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { start_date: start, end_date: end, source, group, category }
        })
      ]);

      setLedgerData(ledgerRes.data);
      setCategoryData(categoryRes.data);
      setGroupData(groupRes.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch expense data');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchData();
  }, [dateFilter, customStartDate, customEndDate, sourceFilter, groupFilter, categoryFilter]);

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const exportToExcel = () => {
    if (!ledgerData) return;
    
    const { start, end } = getDateRange();
    let csv = `Expense Ledger Report\n`;
    csv += `Period: ${start} to ${end}\n`;
    csv += `Source: ${sourceFilter === 'all' ? 'All Sources' : sourceFilter}\n`;
    csv += `Group: ${groupFilter === 'all' ? 'All Groups' : groupFilter}\n`;
    csv += `Category: ${categoryFilter === 'all' ? 'All Categories' : categoryFilter}\n\n`;
    
    csv += `Summary\n`;
    csv += `Total Expenses,₹${ledgerData.total.toLocaleString('en-IN')}\n`;
    csv += `Cashier Expenses,₹${ledgerData.cashier_total.toLocaleString('en-IN')}\n`;
    csv += `Bank Expenses,₹${ledgerData.bank_total.toLocaleString('en-IN')}\n\n`;
    
    csv += `Date,Source,Group,Category,Amount,Remarks\n`;
    ledgerData.expenses.forEach(exp => {
      csv += `${exp.date},"${exp.source_detail}","${exp.group}","${exp.category}",${exp.amount},"${exp.remarks || ''}"\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `Expense_Ledger_${start}_to_${end}.csv`);
    toast.success('Excel file downloaded!');
  };

  const exportToPDF = () => {
    if (!ledgerData || !groupData) return;
    
    const { start, end } = getDateRange();
    const sourceLabel = sourceFilter === 'all' ? 'All Sources' : sourceFilter === 'cashier' ? 'Cashier Only' : 'Bank Only';
    const groupLabel = groupFilter === 'all' ? 'All Groups' : groupFilter;
    const categoryLabel = categoryFilter === 'all' ? 'All Categories' : categoryFilter;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Expense Ledger Report</title>
        <style>
          @page { size: portrait; margin: 15mm; }
          body { font-family: Arial, sans-serif; padding: 10px; font-size: 11px; }
          h1 { text-align: center; color: #dc2626; font-size: 18px; margin-bottom: 5px; }
          h2 { text-align: center; font-size: 14px; color: #333; margin-top: 0; }
          .header { text-align: center; margin-bottom: 15px; }
          .period { color: #666; margin-bottom: 4px; font-size: 12px; }
          .filters { color: #666; margin-bottom: 8px; font-size: 11px; }
          .summary { display: flex; justify-content: space-around; margin: 15px 0; padding: 10px; background: #fef2f2; border-radius: 8px; }
          .summary-item { text-align: center; }
          .summary-label { font-size: 10px; color: #666; }
          .summary-value { font-size: 16px; font-weight: bold; }
          .summary-value.total { color: #dc2626; }
          .summary-value.cashier { color: #ea580c; }
          .summary-value.bank { color: #2563eb; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; }
          th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; font-size: 10px; }
          th { background-color: #dc2626; color: white; }
          .amount { text-align: right; }
          .group-row { background-color: #fef2f2; font-weight: bold; }
          .category-row { background-color: #f9fafb; }
          .category-row td:first-child { padding-left: 20px; }
          .grand-total { background-color: #dc2626; color: white; font-weight: bold; font-size: 11px; }
          .footer { margin-top: 20px; text-align: center; color: #666; font-size: 9px; }
          .section-title { font-size: 12px; font-weight: bold; margin-top: 20px; margin-bottom: 10px; color: #333; border-bottom: 2px solid #dc2626; padding-bottom: 5px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>HOPPERS FINANCE DSR</h1>
          <h2>Expense Ledger Report</h2>
          <p class="period">Period: ${new Date(start).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} to ${new Date(end).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
          <p class="filters">Source: ${sourceLabel} | Group: ${groupLabel} | Category: ${categoryLabel}</p>
        </div>
        
        <div class="summary">
          <div class="summary-item">
            <div class="summary-label">Total Expenses</div>
            <div class="summary-value total">₹${ledgerData.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Cashier Expenses</div>
            <div class="summary-value cashier">₹${ledgerData.cashier_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Bank Expenses</div>
            <div class="summary-value bank">₹${ledgerData.bank_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
          </div>
        </div>

        <div class="section-title">Expenses by Group</div>
        <table>
          <thead>
            <tr>
              <th>Group / Category</th>
              <th class="amount">Cashier (₹)</th>
              <th class="amount">Bank (₹)</th>
              <th class="amount">Total (₹)</th>
            </tr>
          </thead>
          <tbody>
            ${groupData.by_group.map(group => `
              <tr class="group-row">
                <td>${group.group}</td>
                <td class="amount">${group.cashier_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                <td class="amount">${group.bank_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                <td class="amount">${group.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              </tr>
              ${group.categories.map(cat => `
                <tr class="category-row">
                  <td>└ ${cat.category}</td>
                  <td class="amount">${cat.cashier_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td class="amount">${cat.bank_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td class="amount">${cat.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                </tr>
              `).join('')}
            `).join('')}
            <tr class="grand-total">
              <td>GRAND TOTAL</td>
              <td class="amount">${groupData.cashier_grand_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              <td class="amount">${groupData.bank_grand_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              <td class="amount">${groupData.grand_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
          </tbody>
        </table>

        <div class="section-title">Transaction Details</div>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Source</th>
              <th>Group</th>
              <th>Category</th>
              <th class="amount">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            ${ledgerData.expenses.map(exp => `
              <tr>
                <td>${new Date(exp.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                <td>${exp.source_detail}</td>
                <td>${exp.group}</td>
                <td>${exp.category}</td>
                <td class="amount">${exp.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              </tr>
            `).join('')}
            <tr class="grand-total">
              <td colspan="4">TOTAL (${ledgerData.count} transactions)</td>
              <td class="amount">${ledgerData.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
          </tbody>
        </table>

        <div class="footer">
          <p>Generated on ${new Date().toLocaleString('en-IN')}</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
    toast.success('PDF ready for print!');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="expense-ledger-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <TrendingDown className="w-5 h-5 text-red-600" />
            Expense Ledger
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-lg items-center">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium">Period:</span>
            </div>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-40" data-testid="date-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="this_week">This Week</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="last_month">Last Month</SelectItem>
                <SelectItem value="this_year">This Year</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>

            {dateFilter === 'custom' && (
              <>
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-40"
                />
                <span className="text-gray-500">to</span>
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-40"
                />
              </>
            )}
          </div>

          {/* Second row of filters */}
          <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-lg items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Source:</span>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="w-36" data-testid="source-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="cashier">Cashier Only</SelectItem>
                  <SelectItem value="bank">Bank Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Group:</span>
              <Select value={groupFilter} onValueChange={setGroupFilter}>
                <SelectTrigger className="w-48" data-testid="group-filter">
                  <SelectValue placeholder="All Groups" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Groups</SelectItem>
                  {allGroups.map(g => (
                    <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Category:</span>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-48" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {filteredCategories.map(c => (
                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(groupFilter !== 'all' || categoryFilter !== 'all' || sourceFilter !== 'all') && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setGroupFilter('all');
                  setCategoryFilter('all');
                  setSourceFilter('all');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                Clear Filters
              </Button>
            )}

            <div className="flex-1" />

            <Button variant="outline" size="sm" onClick={exportToExcel} data-testid="export-excel-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
              Excel
            </Button>
            <Button variant="outline" size="sm" onClick={exportToPDF} data-testid="export-pdf-btn">
              <FileText className="w-4 h-4 mr-2 text-red-600" />
              PDF
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingDown className="w-4 h-4 text-red-600" />
                  <p className="text-xs text-red-600 font-medium">Total Expenses</p>
                </div>
                <p className="text-2xl font-bold text-red-700" data-testid="total-expenses">
                  {formatCurrency(ledgerData?.total || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-orange-600" />
                  <p className="text-xs text-orange-600 font-medium">Cashier Expenses</p>
                </div>
                <p className="text-2xl font-bold text-orange-700" data-testid="cashier-expenses">
                  {formatCurrency(ledgerData?.cashier_total || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="w-4 h-4 text-blue-600" />
                  <p className="text-xs text-blue-600 font-medium">Bank Expenses</p>
                </div>
                <p className="text-2xl font-bold text-blue-700" data-testid="bank-expenses">
                  {formatCurrency(ledgerData?.bank_total || 0)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Tabs for different views */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="by-group">By Group</TabsTrigger>
              <TabsTrigger value="by-category">By Category</TabsTrigger>
              <TabsTrigger value="details">All Transactions</TabsTrigger>
            </TabsList>

            {/* By Group Tab */}
            <TabsContent value="by-group" className="mt-4">
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-100">
                      <TableHead className="font-semibold">Expense Group</TableHead>
                      <TableHead className="text-right font-semibold">Cashier</TableHead>
                      <TableHead className="text-right font-semibold">Bank</TableHead>
                      <TableHead className="text-right font-semibold">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupData?.by_group?.map((group) => (
                      <>
                        <TableRow 
                          key={group.group} 
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => toggleGroup(group.group)}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {expandedGroups[group.group] ? 
                                <ChevronDown className="w-4 h-4" /> : 
                                <ChevronRight className="w-4 h-4" />
                              }
                              {group.group}
                              <Badge variant="outline" className="ml-2">{group.count}</Badge>
                            </div>
                          </TableCell>
                          <TableCell className="text-right text-orange-600">
                            {formatCurrency(group.cashier_total)}
                          </TableCell>
                          <TableCell className="text-right text-blue-600">
                            {formatCurrency(group.bank_total)}
                          </TableCell>
                          <TableCell className="text-right font-bold text-red-600">
                            {formatCurrency(group.total)}
                          </TableCell>
                        </TableRow>
                        {expandedGroups[group.group] && group.categories?.map((cat) => (
                          <TableRow key={`${group.group}-${cat.category}`} className="bg-gray-50">
                            <TableCell className="pl-10 text-gray-600">
                              └ {cat.category}
                            </TableCell>
                            <TableCell className="text-right text-orange-500">
                              {formatCurrency(cat.cashier_total)}
                            </TableCell>
                            <TableCell className="text-right text-blue-500">
                              {formatCurrency(cat.bank_total)}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(cat.total)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    ))}
                    {/* Grand Total Row */}
                    <TableRow className="bg-red-100 font-bold">
                      <TableCell>GRAND TOTAL</TableCell>
                      <TableCell className="text-right text-orange-700">
                        {formatCurrency(groupData?.cashier_grand_total || 0)}
                      </TableCell>
                      <TableCell className="text-right text-blue-700">
                        {formatCurrency(groupData?.bank_grand_total || 0)}
                      </TableCell>
                      <TableCell className="text-right text-red-700 text-lg">
                        {formatCurrency(groupData?.grand_total || 0)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* By Category Tab */}
            <TabsContent value="by-category" className="mt-4">
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-100">
                      <TableHead className="font-semibold">Category</TableHead>
                      <TableHead className="font-semibold">Group</TableHead>
                      <TableHead className="text-right font-semibold">Cashier</TableHead>
                      <TableHead className="text-right font-semibold">Bank</TableHead>
                      <TableHead className="text-right font-semibold">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categoryData?.by_category?.map((cat) => (
                      <TableRow key={cat.category}>
                        <TableCell className="font-medium">{cat.category}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{cat.group}</Badge>
                        </TableCell>
                        <TableCell className="text-right text-orange-600">
                          {formatCurrency(cat.cashier_total)}
                        </TableCell>
                        <TableCell className="text-right text-blue-600">
                          {formatCurrency(cat.bank_total)}
                        </TableCell>
                        <TableCell className="text-right font-bold text-red-600">
                          {formatCurrency(cat.total)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Grand Total Row */}
                    <TableRow className="bg-red-100 font-bold">
                      <TableCell colSpan={2}>GRAND TOTAL</TableCell>
                      <TableCell className="text-right text-orange-700">
                        {formatCurrency(categoryData?.cashier_grand_total || 0)}
                      </TableCell>
                      <TableCell className="text-right text-blue-700">
                        {formatCurrency(categoryData?.bank_grand_total || 0)}
                      </TableCell>
                      <TableCell className="text-right text-red-700 text-lg">
                        {formatCurrency(categoryData?.grand_total || 0)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* All Transactions Tab */}
            <TabsContent value="details" className="mt-4">
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-100">
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold">Source</TableHead>
                      <TableHead className="font-semibold">Group</TableHead>
                      <TableHead className="font-semibold">Category</TableHead>
                      <TableHead className="text-right font-semibold">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledgerData?.expenses?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                          No expenses in this period
                        </TableCell>
                      </TableRow>
                    ) : (
                      ledgerData?.expenses?.map((exp) => (
                        <TableRow key={exp.id}>
                          <TableCell>
                            {new Date(exp.date).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={exp.source === 'cashier' ? 'bg-orange-50 text-orange-700' : 'bg-blue-50 text-blue-700'}
                            >
                              {exp.source === 'cashier' ? <Users className="w-3 h-3 mr-1" /> : <Building2 className="w-3 h-3 mr-1" />}
                              {exp.source_detail}
                            </Badge>
                          </TableCell>
                          <TableCell>{exp.group}</TableCell>
                          <TableCell>{exp.category}</TableCell>
                          <TableCell className="text-right font-semibold text-red-600">
                            {formatCurrency(exp.amount)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                    {/* Grand Total Row */}
                    {ledgerData?.expenses?.length > 0 && (
                      <TableRow className="bg-red-100 font-bold">
                        <TableCell colSpan={4}>TOTAL ({ledgerData.count} transactions)</TableCell>
                        <TableCell className="text-right text-red-700 text-lg">
                          {formatCurrency(ledgerData?.total || 0)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExpenseLedger;
